import { ReactNode } from "react"
import { create } from "zustand"

import { ClientForm } from "@/components/clients/use-create-client-dialog"
import { ContactForm } from "@/components/contacts/use-create-contact-dialog"
import { appDialogClasses, type AppDialogVariant } from "@/components/dialogs/app-dialog-config"
import { popDialogEntry, shouldPreserveDialogsOnNavigation } from "@/components/dialogs/dialog-behavior"
import { ComposeEmail } from "@/components/email/compose"
import { NoteDialog } from "@/components/notes/note-dialog"
import { ProspectForm } from "@/components/prospects/use-create-prospect-dialog"
import CreateTaskDialog from "@/components/tasks/create/dialog"

import { useNoteDialogStore } from "./use-note-dialog-store"
import { usePinnedDialogStore } from "./use-pinned-dialog-store"

export interface DialogEntry {
  id: string
  bookId?: string
  dismiss: import("@/components/dialogs/dialog-behavior").DismissalPolicy
  returnFocus: Element | null
  variant: import("@/components/dialogs/app-dialog-config").AppDialogVariant
  hasAccessibleTitle?: boolean
  content: ReactNode
  dialogContentClasses: string
  scrollable: boolean
  preventEscape: boolean
  afterClose: () => void
  preserveOnNavigation: boolean
}

type OpenDialogOptions = {
  content: ReactNode
  bookId?: string
  closeCallback?: () => void
  preserveOnNavigation?: boolean
} & (
  | {
      variant: Exclude<AppDialogVariant, "alert" | "destroy" | "custom">
      dialogContentClasses?: never
      scrollable?: never
    }
  | {
      variant: "custom"
      dialogContentClasses?: string
      scrollable?: boolean
    }
)

interface GlobalDialogState {
  isOpen: boolean
  contentStack: DialogEntry[]
  dialogContentClasses: string
  pinned: boolean
  preventEscape: boolean
  scrollable?: boolean
  openDialog: (params: OpenDialogOptions) => void
  popDialog: () => void
  closeDialog: () => void
  closeDialogsOnNavigation: () => void
  pinDialog: (id: string, type: "email" | "task" | "client" | "prospect" | "contact" | "note", bookId?: string) => void
  createDialog: (id: string, type: "email" | "task" | "client" | "prospect" | "contact" | "note") => ReactNode
  setPreventEscape: (preventEscape: boolean, entryId?: string) => void
  setDialogContentClasses: (contentClasses: string, entryId?: string) => void
  setEntryVariant: (entryId: string, variant: DialogEntry["variant"], dismiss: DialogEntry["dismiss"]) => void
}

export const useGlobalDialogStore = create<GlobalDialogState>((set, get) => ({
  isOpen: false,
  contentStack: [],
  dialogContentClasses: "",
  pinned: false,
  preventEscape: false,
  scrollable: false,
  openDialog: ({
    variant,
    content,
    bookId,
    closeCallback = () => {},
    preserveOnNavigation = false,
    dialogContentClasses: customClasses = "",
    scrollable = false,
  }) => {
    const dialogContentClasses = variant === "custom" ? customClasses : appDialogClasses(variant)
    set((state) => ({
      isOpen: true,
      contentStack: [
        ...state.contentStack,
        {
          id: crypto.randomUUID(),
          bookId,
          returnFocus: typeof document === "undefined" ? null : document.activeElement,
          variant,
          dismiss: "all",
          content,
          afterClose: closeCallback,
          preserveOnNavigation,
          dialogContentClasses,
          scrollable,
          preventEscape: false,
        },
      ],
      dialogContentClasses,
      scrollable,
      preventEscape: false,
      pinned: false,
    }))
  },
  popDialog: () => {
    let callback: (() => void) | undefined
    let returnFocus: Element | null = null
    set((state) => {
      const { removed, remaining: contentStack, restored: previous } = popDialogEntry(state.contentStack)
      callback = removed?.afterClose
      returnFocus = removed?.returnFocus ?? null
      if (removed) entryCloseAttemptHandlers.delete(removed.id)
      return {
        contentStack,
        isOpen: contentStack.length > 0,
        dialogContentClasses: previous?.dialogContentClasses ?? "",
        scrollable: previous?.scrollable ?? false,
        preventEscape: previous?.preventEscape ?? false,
      }
    })
    callback?.()
    requestAnimationFrame(() => {
      if (returnFocus instanceof HTMLElement && returnFocus.isConnected) returnFocus.focus()
    })
  },
  closeDialog: () => {
    entryCloseAttemptHandlers.clear()
    let callbacks: Array<() => void> = []
    set((state) => {
      callbacks = state.contentStack.map((entry) => entry.afterClose).reverse()
      return {
        isOpen: false,
        contentStack: [],
        dialogContentClasses: "",
        pinned: false,
        scrollable: false,
        preventEscape: false,
      }
    })
    callbacks.forEach((callback) => callback())
  },
  closeDialogsOnNavigation: () => {
    const state = get()
    if (shouldPreserveDialogsOnNavigation(state.contentStack, getCloseAttemptHandler)) return
    state.closeDialog()
  },
  pinDialog: (id, type = "email", bookId) => {
    const state = get()
    const active = state.contentStack.at(-1)
    if (!active) return
    usePinnedDialogStore.getState().pinNewDialog({
      content: { content: state.createDialog(id, type), id, type, bookId },
      dialogContentClasses: active.dialogContentClasses,
    })
    state.popDialog()
    set({ pinned: true })
  },
  setPreventEscape: (preventEscape: boolean, entryId?: string) =>
    set((state) => ({
      preventEscape: !entryId || state.contentStack.at(-1)?.id === entryId ? preventEscape : state.preventEscape,
      contentStack: state.contentStack.map((entry, index) =>
        (entryId ? entry.id === entryId : index === state.contentStack.length - 1) ? { ...entry, preventEscape } : entry
      ),
    })),

  createDialog: (id: string, type: "email" | "task" | "client" | "prospect" | "contact" | "note") => {
    if (type === "email") {
      return <ComposeEmail draftId={id} />
    } else if (type === "task") {
      return <CreateTaskDialog id={id} />
    } else if (type === "client") {
      return <ClientForm id={id} />
    } else if (type === "prospect") {
      return <ProspectForm id={id} />
    } else if (type === "contact") {
      return <ContactForm id={id} />
    } else if (type === "note") {
      const { drafts } = useNoteDialogStore.getState()
      const draft = drafts[id]
      const noteId = draft?.noteId ?? (id.startsWith("note-draft-") ? "" : id)
      return (
        <NoteDialog
          noteId={noteId}
          dialogId={id}
          householdId={draft?.selectedHouseholdId ?? undefined}
          contactId={draft?.selectedContactId ?? undefined}
        />
      )
    }
    return null
  },
  setEntryVariant: (entryId, variant, dismiss) =>
    set((state) => ({
      contentStack: state.contentStack.map((entry) =>
        entry.id === entryId ? { ...entry, variant, dismiss, hasAccessibleTitle: true } : entry
      ),
    })),
  setDialogContentClasses: (contentClasses: string, entryId?: string) =>
    set((state) => ({
      dialogContentClasses:
        !entryId || state.contentStack.at(-1)?.id === entryId ? contentClasses : state.dialogContentClasses,
      contentStack: state.contentStack.map((entry, index) =>
        (entryId ? entry.id === entryId : index === state.contentStack.length - 1)
          ? { ...entry, dialogContentClasses: contentClasses }
          : entry
      ),
    })),
}))

// Guards are keyed by host entry; local dialogs use their own React context registry.
const entryCloseAttemptHandlers = new Map<string, () => void>()
export const getCloseAttemptHandler = (entryId?: string | null) => {
  const id = entryId ?? useGlobalDialogStore.getState().contentStack.at(-1)?.id
  return id ? entryCloseAttemptHandlers.get(id) : undefined
}
export const setCloseAttemptHandler = (handler: (() => void) | null, entryId: string) => {
  if (handler) entryCloseAttemptHandlers.set(entryId, handler)
  else entryCloseAttemptHandlers.delete(entryId)
}

// Pinned dialogs stay mounted alongside the active global dialog, so their close guards are keyed
// by dialog id (PinnedDialog routes each pinned close through the matching handler) separately from
// the global entry registry — otherwise a pinned dialog would hijack the global dialog's close.
const _pinnedCloseAttemptHandlers: Record<string, () => void> = {}
export const getPinnedCloseAttemptHandler = (id: string) => _pinnedCloseAttemptHandlers[id] ?? null
export const setPinnedCloseAttemptHandler = (id: string, handler: (() => void) | null) => {
  if (handler) {
    _pinnedCloseAttemptHandlers[id] = handler
  } else {
    delete _pinnedCloseAttemptHandlers[id]
  }
}
